<template>
  <div>
    <div class="md:flex flex-wrap md:justify-center lg:justify-between justify-between">
      <div class="w-full">
        <h1 class="font-bold pt-5 lg:text-left text-center">
          Quiz & Exam Grades
        </h1>
        <d-message v-if="serverErrorMessage" type="error">
          {{ serverErrorMessage }}
        </d-message>
        
        <!-- Tabs for Quizzes and Exams -->
        <div class="tabs tabs-bordered mt-4" v-if="!loading">
          <button 
            class="tab"
            :class="{ 'tab-active': activeTab === 'quizzes' }"
            @click="activeTab = 'quizzes'"
          >
            Quizzes ({{ quizGrades.length }})
          </button>
          <button 
            class="tab"
            :class="{ 'tab-active': activeTab === 'exams' }"
            @click="activeTab = 'exams'"
          >
            Exams ({{ examGrades.length }})
          </button>
        </div>

        <!-- Quiz Tab Content -->
        <div v-if="!loading && activeTab === 'quizzes'">
          <d-table 
            :data="quizGrades" 
            :columns="quizTableColumns"
            :default-sort="['dateDisplay']" 
            default-sort-direction="desc">
            <template #default="{ row }">
              <td>
                {{ row.name || row.quizName }}
              </td>
              <td>{{ row.date || row.dateDisplay }}</td>
              <td>
                {{ row.score || row.scoreDisplay }}
                <span v-if="row.passed !== null && row.passed !== undefined" 
                      :class="row.passed ? 'text-success' : 'text-danger'" 
                      class="ml-2 font-weight-bold">
                  ({{ row.passed ? 'PASSED' : 'FAILED' }})
                </span>
              </td>
              <td>{{ row.feedback }}</td>
            </template>
          </d-table>
        </div>

        <!-- Exam Tab Content -->
        <div v-if="!loading && activeTab === 'exams'">
          <d-table 
            :data="examGrades" 
            :columns="examTableColumns"
            :default-sort="['dateDisplay']" 
            default-sort-direction="desc">
            <template #default="{ row }">
              <td>
                {{ row.name || row.examName }}
              </td>
              <td>{{ row.date || row.dateDisplay }}</td>
              <td>
                {{ getExamScoreDisplay(row) }}
                <span v-if="getExamStatus(row) === 'Not Started'" 
                      class="ml-2 font-weight-bold text-secondary">
                  (NOT STARTED)
                </span>
                <span v-else-if="getExamStatus(row) === 'In Progress'" 
                      class="ml-2 font-weight-bold text-primary">
                  (IN PROGRESS)
                </span>
                <span v-else-if="getExamStatus(row) === 'Completed' && row.passed !== null && row.passed !== undefined" 
                      :class="row.passed ? 'text-success' : 'text-danger'" 
                      class="ml-2 font-weight-bold">
                  ({{ row.passed ? 'PASSED' : 'FAILED' }})
                </span>
              </td>
              <td>{{ row.feedback }}</td>
            </template>
          </d-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useAuthStore } from '@stores/auth';
export default {
  parent: true,
  data() {
    return {
      quizGrades: [],
      examGrades: [],
      loading: true,
      auth: null,
      activeTab: 'quizzes',
      serverErrorMessage: '',
      quizTableColumns: [
        { field: 'name', label: 'Quiz Name', sortable: true },
        { field: 'date', label: 'Date', sortable: true },
        { field: 'score', label: 'Grade', sortable: false },
        { field: 'feedback', label: 'Feedback', sortable: false }
      ],
      examTableColumns: [
        { field: 'name', label: 'Exam Name', sortable: true },
        { field: 'date', label: 'Date', sortable: true },
        { field: 'score', label: 'Grade', sortable: false },
        { field: 'feedback', label: 'Feedback', sortable: false }
      ]
    };
  },

  computed: {
    profile() {
      return this.auth ? this.auth.profile || {} : {};
    }
  },

  mounted() {
    this.auth = useAuthStore();
    this.fetchGrades();
  },

  methods: {
    setUserProfile(profile) {
      this.auth.setUserProfile(profile);
    },

    async fetchGrades() {
      try {
        this.loading = true;
        this.serverErrorMessage = '';

        // Fetch quiz grades and all eligible exams separately
        const [quizResponse, examResponse] = await Promise.all([
          axios.get('/api/transcript/all-grades'),
          axios.get('/api/transcript/all-eligible-exams')
        ]);

        // Filter quiz grades from the all-grades endpoint
        this.quizGrades = quizResponse.data.filter(grade => grade.type === 'Quiz');
        
        // Use the new endpoint that includes all eligible exams
        this.examGrades = examResponse.data.map(exam => ({
          name: exam.examName || exam.contentListingName,
          date: exam.isStarted ? exam.startedAt.split('T')[0] : '',
          feedback: exam.instructorFeedback,
          questionsCorrect: exam.questionsCorrect,
          questionsTotal: exam.questionsTotal,
          questionsIncorrect: exam.questionsIncorrect,
          passed: exam.passed,
          examId: exam.examId,
          isStarted: exam.isStarted
        }));

      } catch (error) {
        console.error('Error fetching grades:', error);
        this.serverErrorMessage = 'Failed to load grades. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    getExamStatus(exam) {
      // Check if exam has not been started
      if (!exam.isStarted) {
        return 'Not Started';
      }

      // Check if we have the data needed to determine completion status
      if (exam.questionsCorrect !== undefined && 
          exam.questionsIncorrect !== undefined && 
          exam.questionsTotal !== undefined) {
        
        const questionsCorrect = parseInt(exam.questionsCorrect) || 0;
        const questionsIncorrect = parseInt(exam.questionsIncorrect) || 0;
        const questionsTotal = parseInt(exam.questionsTotal) || 0;
        const questionsAnswered = questionsCorrect + questionsIncorrect;
        
        console.log(`Exam ${exam.name}: ${questionsCorrect} correct + ${questionsIncorrect} incorrect = ${questionsAnswered} answered out of ${questionsTotal} total`);
        
        if (questionsTotal > 0) {
          // If not all questions have been answered, it's in progress
          if (questionsAnswered < questionsTotal) {
            return 'In Progress';
          }
          
          // If all questions have been answered, it's completed
          return 'Completed';
        }
      }
      
      return 'In Progress';
    },

    getExamScoreDisplay(exam) {
      const status = this.getExamStatus(exam);
      
      // No score for not started exams
      if (status === 'Not Started') {
        return '';
      }
      
      // Only show score for completed exams
      if (status === 'Completed') {
        const questionsCorrect = parseInt(exam.questionsCorrect) || 0;
        const questionsTotal = parseInt(exam.questionsTotal) || 0;
        
        if (questionsTotal > 0) {
          const percentage = ((questionsCorrect / questionsTotal) * 100).toFixed(1);
          return `${questionsCorrect}/${questionsTotal} (${percentage}%)`;
        }
      }
      
      // Return empty string for in-progress exams (no score shown)
      return '';
    }
  },
};
</script>
